import React, { useCallback, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';

import { Icons, PageTitle, Table } from '@components/common';
import { getColumns, TFileExtensions } from '@components/routes/AdminPanel/Reports/ApiDocumentation/getColumns';

import { useApiDocumentation } from '@hooks/reports/useApiDocumentation';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { ISortBy, ITableSort, ITableSortInitialState } from '@interfaces/components/table';

import { EAdminPanelPageTitles, EApiDocsGroups, ESnackbarTypes, TApiDocsGroups } from '@enums/common';

import { responseMessages } from '@constants/responseMessages';

import styles from './ApiDocumentation.module.scss';

interface IData {
	title: string;
	platform: TApiDocsGroups;
	actions: string;
}

const data: IData[] = [
	{
		title: 'iDAMS',
		platform: EApiDocsGroups.iDams,
		actions: 'Actions',
	},
	{
		title: 'Flex-5G',
		platform: EApiDocsGroups.flex5G,
		actions: 'Actions',
	},
	{
		title: 'Others',
		platform: EApiDocsGroups.other,
		actions: 'Actions',
	},
];

function ApiDocumentation() {
	const dispatch = useAppDispatch();

	const onSuccess = () => {
		dispatch(setSnackbar({
			message: responseMessages.apiDocsExported,
			type: ESnackbarTypes.success,
		}));
	};

	const onError = () => {
		dispatch(setSnackbar({
			message: responseMessages.apiDocsExportedError,
			type: ESnackbarTypes.error,
		}));
	};

	const {
		isLoading,
		generateApiDocumentation,
	} = useApiDocumentation(onSuccess, onError);

	const handleExport = useCallback((platform: TApiDocsGroups, fileType: TFileExtensions) => {
		void generateApiDocumentation({
			fileType: fileType,
			groups: [platform],
		});
		setPlatform(undefined);
	}, [
		generateApiDocumentation,
	]);

	const [platform, setPlatform] = useState<TApiDocsGroups>();

	const handleSetPlatform = useCallback((value: TApiDocsGroups) => () => {
		if (platform === value) setPlatform(undefined);
		else setPlatform(value);
	}, [platform]);

	const nodeRef = React.useRef<HTMLDivElement>(null);

	const columns: ColumnDef<any>[] = useMemo(() => getColumns({
		nodeRef,
		setPlatform,
		platform,
		handleExport,
		handleSetPlatform,
	}), [
		handleExport,
		handleSetPlatform,
		platform,
	]);

	const [sortState, setSortState] = useState<ITableSortInitialState[]>([
		{
			id: 'title',
			desc: false,
		},
	]);

	const sort: ITableSort = {
		initialState: [
			{
				id: 'title',
				desc: false,
			},
		],
		handleSort: (sortBy: ISortBy[]) => {
			setSortState(sortBy);
		},
	};

	const sortedData = useMemo(() => {
		return data.map(item => item).sort((a: IData, b: IData) => {
			const sort = sortState[0];

			const sortId = sort?.id as keyof IData;

			if (!sortId) return 0;

			if (a[sortId] > b[sortId]) return sort.desc ? -1 : 1;

			if (a[sortId] < b[sortId]) return sort.desc ? 1 : -1;

			return 0;
		});
	}, [
		sortState,
	]);

	return (
		<div className={styles.apiDocumentation}>
			<PageTitle
				icon={Icons.consistencyActive()}
				title={EAdminPanelPageTitles.APIDocumentation}
			/>
			<div className={styles.apiDocumentationTable}>
				<Table
					columns={columns}
					data={sortedData}
					isLoading={isLoading}
					isWithPagination={false}
					isWithoutActions
					isWithoutCheckbox
					isWithoutDeleteAction
					isWithoutFilter
					sort={sort}
				/>
			</div>
		</div>
	);
}

export default ApiDocumentation;
